import React, { useCallback, useState } from 'react';
import { Grid, Save, RefreshCw, Download, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import RoomParametersSection from './RoomParametersSection';
import TileParametersSection from './TileParametersSection';
import InstallationParametersSection from './InstallationParametersSection';
import AdditionalMaterialsSection from './AdditionalMaterialsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isNumber } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета плитки
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  generateMaterialsData,
  areaTypes,
  tileTypes,
  layoutPatterns,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalcName, setSaveCalcName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем высоту помещения при изменении типа помещения
      if (name === 'areaType') {
        const selectedAreaType = areaTypes.find(aType => aType.id === fieldValue);
        if (selectedAreaType) {
          newState.roomHeight = selectedAreaType.defaultHeight.toString();
        }
      }

      // Обновляем коэффициент запаса при изменении типа укладки
      if (name === 'layoutPattern') {
        const selectedPattern = layoutPatterns.find(pattern => pattern.id === fieldValue);
        if (selectedPattern) {
          newState.wasteFactor = selectedPattern.wasteFactor.toString();
        }
      }

      // Вычисляем площадь пачки при изменении размеров плитки или количества в упаковке
      if (name === 'tileWidth' || name === 'tileLength' || name === 'tilesInPackage') {
        const tileWidth = parseFloatOrDefault(name === 'tileWidth' ? value : prev.tileWidth, 0);
        const tileLength = parseFloatOrDefault(name === 'tileLength' ? value : prev.tileLength, 0);
        const tilesInPackage = parseFloatOrDefault(name === 'tilesInPackage' ? value : prev.tilesInPackage, 0);
        
        if (tileWidth > 0 && tileLength > 0 && tilesInPackage > 0) {
          // Преобразуем см в м²
          const tileAreaInM2 = (tileWidth * tileLength) / 10000;
          const packageArea = tileAreaInM2 * tilesInPackage;
          newState.packageArea = packageArea.toFixed(2);
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [areaTypes, layoutPatterns, errors, setErrors, setFormData]);

  // Специальный обработчик для массивов (стены для облицовки)
  const handleWallsChange = useCallback((walls) => {
    setFormData(prev => {
      return { ...prev, wallsToTile: walls };
    });
  }, [setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      roomWidth, roomLength, roomHeight, additionalArea,
      tileWidth, tileLength, tileThickness, tilesInPackage, packageArea, packagePrice,
      seamWidth, wasteFactor
    } = formData;

    // Парсинг числовых значений
    const numRoomWidth = parseFloatOrDefault(roomWidth, -1);
    const numRoomLength = parseFloatOrDefault(roomLength, -1);
    const numRoomHeight = parseFloatOrDefault(roomHeight, -1);
    const numAdditionalArea = parseFloatOrDefault(additionalArea, 0);

    const numTileWidth = parseFloatOrDefault(tileWidth, -1);
    const numTileLength = parseFloatOrDefault(tileLength, -1);
    const numTileThickness = parseFloatOrDefault(tileThickness, -1);
    const numTilesInPackage = parseFloatOrDefault(tilesInPackage, -1);
    const numPackageArea = parseFloatOrDefault(packageArea, -1);
    const numPackagePrice = parseFloatOrDefault(packagePrice, -1);
    
    const numSeamWidth = parseFloatOrDefault(seamWidth, -1);
    const numWasteFactor = parseFloatOrDefault(wasteFactor, -1);

    // Валидация параметров помещения
    if (numRoomWidth <= 0) newErrors.roomWidth = 'Ширина должна быть положительной.';
    if (!newErrors.roomWidth && numRoomWidth > 50) newErrors.roomWidth = 'Максимальная ширина 50 м.';
    
    if (numRoomLength <= 0) newErrors.roomLength = 'Длина должна быть положительной.';
    if (!newErrors.roomLength && numRoomLength > 50) newErrors.roomLength = 'Максимальная длина 50 м.';
    
    if (numRoomHeight <= 0) newErrors.roomHeight = 'Высота должна быть положительной.';
    if (!newErrors.roomHeight && numRoomHeight > 10) newErrors.roomHeight = 'Максимальная высота 10 м.';
    
    if (numAdditionalArea < 0) newErrors.additionalArea = 'Площадь не может быть отрицательной.';
    if (!newErrors.additionalArea && numAdditionalArea > 200) newErrors.additionalArea = 'Превышена максимальная площадь.';

    // Валидация параметров плитки
    if (numTileWidth <= 0) newErrors.tileWidth = 'Ширина должна быть положительной.';
    if (!newErrors.tileWidth && numTileWidth > 120) newErrors.tileWidth = 'Максимальная ширина 120 см.';
    
    if (numTileLength <= 0) newErrors.tileLength = 'Длина должна быть положительной.';
    if (!newErrors.tileLength && numTileLength > 120) newErrors.tileLength = 'Максимальная длина 120 см.';
    
    if (numTileThickness <= 0) newErrors.tileThickness = 'Толщина должна быть положительной.';
    if (!newErrors.tileThickness && numTileThickness > 5) newErrors.tileThickness = 'Максимальная толщина 5 см.';
    
    if (numTilesInPackage <= 0) newErrors.tilesInPackage = 'Кол-во должно быть положительным.';
    if (!isNumber(tilesInPackage) || Math.floor(numTilesInPackage) !== numTilesInPackage) {
      newErrors.tilesInPackage = 'Введите целое число.';
    }
    
    if (numPackageArea <= 0) newErrors.packageArea = 'Площадь должна быть положительной.';
    if (numPackagePrice <= 0) newErrors.packagePrice = 'Цена должна быть положительной.';

    // Валидация параметров укладки
    if (numSeamWidth < 0) newErrors.seamWidth = 'Ширина шва не может быть отрицательной.';
    if (!newErrors.seamWidth && numSeamWidth > 20) newErrors.seamWidth = 'Максимальная ширина шва 20 мм.';
    
    if (numWasteFactor < 0) newErrors.wasteFactor = 'Запас не может быть отрицательным.';
    if (!newErrors.wasteFactor && numWasteFactor > 50) newErrors.wasteFactor = 'Максимальный запас 50%.';

    // Валидация параметра работ
    if (formData.includeLaborCost) {
      const numLaborPrice = parseFloatOrDefault(formData.laborPricePerMeter, -1);
      if (numLaborPrice <= 0) newErrors.laborPricePerMeter = 'Цена должна быть положительной.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета плитки
  const calculateTileCoverage = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const roomWidth = parseFloatOrDefault(formData.roomWidth);
        const roomLength = parseFloatOrDefault(formData.roomLength);
        const roomHeight = parseFloatOrDefault(formData.roomHeight);
        const additionalArea = parseFloatOrDefault(formData.additionalArea, 0);
        
        const tileWidth = parseFloatOrDefault(formData.tileWidth) / 100; // переводим см в метры
        const tileLength = parseFloatOrDefault(formData.tileLength) / 100; // переводим см в метры
        const tileThickness = parseFloatOrDefault(formData.tileThickness);
        const tilesInPackage = parseFloatOrDefault(formData.tilesInPackage);
        const packageArea = parseFloatOrDefault(formData.packageArea);
        const packagePrice = parseFloatOrDefault(formData.packagePrice);
        
        const seamWidth = parseFloatOrDefault(formData.seamWidth) / 1000; // переводим мм в метры
        const wasteFactor = parseFloatOrDefault(formData.wasteFactor) / 100; // переводим % в доли
        
        // --- Расчет площадей ---
        // Площадь пола
        let floorArea = roomWidth * roomLength + additionalArea;
        
        // Площадь стен
        const wallsToTile = formData.wallsToTile || [];
        let wallArea = 0;
        
        // Выбираем стены для облицовки
        if (wallsToTile.includes('all')) {
          // Все стены: периметр * высота
          wallArea = 2 * (roomWidth + roomLength) * roomHeight;
        } else {
          // Отдельные стены
          if (wallsToTile.includes('north') || wallsToTile.includes('south')) {
            wallArea += roomWidth * roomHeight * (wallsToTile.includes('north') && wallsToTile.includes('south') ? 2 : 1);
          }
          if (wallsToTile.includes('east') || wallsToTile.includes('west')) {
            wallArea += roomLength * roomHeight * (wallsToTile.includes('east') && wallsToTile.includes('west') ? 2 : 1);
          }
        }
        
        // Общая площадь укладки
        const totalArea = (wallsToTile.length > 0 ? wallArea : 0) + (wallsToTile.includes('floor') || wallsToTile.length === 0 ? floorArea : 0);
        
        // Площадь одной плитки
        const tileArea = tileWidth * tileLength;
        
        // --- Расчет плитки с учетом запаса ---
        const totalAreaWithWaste = totalArea * (1 + wasteFactor);
        
        // Количество плиток и упаковок
        const tilesNeeded = Math.ceil(totalAreaWithWaste / tileArea);
        const packagesNeeded = Math.ceil(tilesNeeded / tilesInPackage);
        
        // Фактическая площадь плитки с учетом упаковок
        const totalTileArea = packagesNeeded * packageArea;
        
        // Стоимость плитки
        const totalTileCost = packagesNeeded * packagePrice;
        
        // --- Расчет дополнительных материалов ---
        // Расчет затирки
        let groutNeeded = 0;
        let groutCost = 0;
        let seamArea = 0;
        let groutPackages = 0;
        
        if (formData.includeGroutCost) {
          // Расчет длины швов: (ширина помещения / ширина плитки + 1) * высота помещения + 
          //                    (высота помещения / высота плитки + 1) * ширина помещения
          const horizontalSeams = Math.ceil(totalArea / tileWidth);
          const verticalSeams = Math.ceil(totalArea / tileLength);
          
          const totalSeamLength = (horizontalSeams * tileLength + verticalSeams * tileWidth);
          seamArea = totalSeamLength * seamWidth * tileThickness;
          
          // Примерный расход затирки: 0.3-0.5 кг/м² шва
          const groutConsumption = formData.tileType === 'mosaic' ? 0.5 : 0.3; // кг/м²
          groutNeeded = seamArea * groutConsumption;
          
          // Одна упаковка затирки примерно 2-5 кг
          const groutPackageSize = 2; // кг
          groutPackages = Math.ceil(groutNeeded / groutPackageSize);
          
          // Средняя цена затирки 500р/кг
          const groutPricePerKg = 500; // руб/кг
          groutCost = groutNeeded * groutPricePerKg;
        }
        
        // Расчет клея
        let adhesiveNeeded = 0;
        let adhesiveCost = 0;
        let adhesivePackages = 0;
        
        if (formData.includeAdhesiveCost) {
          // Расход клея около 3-5 кг/м²
          const adhesiveConsumption = formData.tileType === 'natural_stone' ? 5 : 
                                      formData.tileType === 'porcelain' ? 4 : 3; // кг/м²
          adhesiveNeeded = totalAreaWithWaste * adhesiveConsumption;
          
          // Одна упаковка клея стандартно 25 кг
          const adhesivePackageSize = 25; // кг
          adhesivePackages = Math.ceil(adhesiveNeeded / adhesivePackageSize);
          
          // Средняя цена клея 30р/кг
          const adhesivePricePerKg = 30; // руб/кг
          adhesiveCost = adhesiveNeeded * adhesivePricePerKg;
        }
        
        // Расчет грунтовки
        let primerNeeded = 0;
        let primerCost = 0;
        let primerPackages = 0;
        
        if (formData.includePrimerCost) {
          // Расход грунтовки около 0.2-0.3 л/м²
          const primerConsumption = 0.25; // л/м²
          primerNeeded = totalAreaWithWaste * primerConsumption;
          
          // Одна упаковка грунтовки стандартно 5 л
          const primerPackageSize = 5; // л
          primerPackages = Math.ceil(primerNeeded / primerPackageSize);
          
          // Средняя цена грунтовки 150р/л
          const primerPricePerLiter = 150; // руб/л
          primerCost = primerNeeded * primerPricePerLiter;
        }
        
        // Расчет стоимости работ
        let laborCost = 0;
        
        if (formData.includeLaborCost) {
          const laborPricePerMeter = parseFloatOrDefault(formData.laborPricePerMeter);
          laborCost = totalAreaWithWaste * laborPricePerMeter;
        }
        
        // Итоговая стоимость
        const totalCost = totalTileCost + adhesiveCost + groutCost + primerCost + laborCost;
        
        // --- Подготовка результатов ---
        const calculatedResults = {
          // Площади
          floorArea,
          wallArea,
          totalArea,
          totalAreaWithWaste,
          seamArea,
          
          // Плитка
          tileArea,
          totalTilesNeeded: tilesNeeded,
          packagesNeeded,
          totalTileArea,
          totalTileCost,
          wasteFactor: wasteFactor * 100,
          
          // Дополнительные материалы
          adhesiveNeeded,
          adhesivePackages,
          adhesiveCost,
          groutNeeded,
          groutPackages,
          groutCost,
          primerNeeded,
          primerPackages,
          primerCost,
          
          // Работа
          laborCost,
          
          // Стоимость
          totalCost
        };
        
        setResults(calculatedResults);
        generateMaterialsData(calculatedResults);
        
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, generateMaterialsData]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      areaType: 'room',
      roomShape: 'rectangle',
      roomWidth: '4',
      roomLength: '5',
      roomHeight: '2.7',
      additionalArea: '0',
      wallsToTile: ['all'],
      
      tileType: 'ceramic',
      tileShape: 'square',
      tileWidth: '30',
      tileLength: '30',
      tileThickness: '0.8',
      tilesInPackage: '10',
      packageArea: '0.9',
      packagePrice: '1200',
      
      layoutPattern: 'straight',
      seamWidth: '2',
      wasteFactor: '10',
      
      includePrimerCost: true,
      includeAdhesiveCost: true,
      includeGroutCost: true,
      
      includeLaborCost: false,
      laborPricePerMeter: '700',
    });
    setResults(null);
    setErrors({});
    setSaveCalcName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    const headers = ['Наименование', 'Количество', 'Упаковок', 'Площадь (м²)', 'Цена (₽)', 'Примечания'];

    // Подготовка данных для экспорта
    const rows = [];
    
    // Добавление сводной информации
    rows.push(['Общая информация', '', '', '', '', '']);
    rows.push(['Площадь помещения', '', '', results.totalArea.toFixed(2), '', 'м²']);
    rows.push(['Площадь с запасом', '', '', results.totalAreaWithWaste.toFixed(2), '', `Включая запас ${results.wasteFactor}%`]);
    rows.push(['', '', '', '', '', '']);
    
    // Добавление основных материалов
    rows.push(['Материалы', '', '', '', '', '']);
    rows.push(['Плитка', results.totalTilesNeeded, results.packagesNeeded, results.totalTileArea.toFixed(2), results.totalTileCost.toFixed(2), `Запас ${results.wasteFactor}%`]);
    
    if (formData.includeAdhesiveCost) {
      rows.push(['Плиточный клей', `${results.adhesiveNeeded.toFixed(2)} кг`, results.adhesivePackages, results.totalTileArea.toFixed(2), results.adhesiveCost.toFixed(2), 'Расчет по средним нормам расхода']);
    }
    
    if (formData.includeGroutCost) {
      rows.push(['Затирка для швов', `${results.groutNeeded.toFixed(2)} кг`, results.groutPackages, (results.seamArea || 0).toFixed(2), results.groutCost.toFixed(2), `Для швов шириной ${formData.seamWidth} мм`]);
    }
    
    if (formData.includePrimerCost) {
      rows.push(['Грунтовка', `${results.primerNeeded.toFixed(2)} л`, results.primerPackages, results.totalArea.toFixed(2), results.primerCost.toFixed(2), 'Для подготовки поверхности']);
    }
    
    if (formData.includeLaborCost) {
      rows.push(['Работа по укладке', '-', '-', results.totalTileArea.toFixed(2), results.laborCost.toFixed(2), `${formData.laborPricePerMeter} ₽/м²`]);
    }
    
    rows.push(['', '', '', '', '', '']);
    rows.push(['Итого', '', '', '', results.totalCost.toFixed(2), '₽']);

    // Преобразование в CSV
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map(row => row.join(';'))].join('\n');
    
    // Экспорт файла
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_плитки.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalcName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalcName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateTileCoverage();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Grid className="w-6 h-6 text-fuchsia-500" />
          Параметры расчета плитки
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Параметры помещения */}
        <RoomParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          handleWallsChange={handleWallsChange}
          errors={errors} 
          areaTypes={areaTypes}
        />

        {/* Параметры плитки */}
        <TileParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          tileTypes={tileTypes}
        />

        {/* Параметры укладки */}
        <InstallationParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          layoutPatterns={layoutPatterns}
        />

        {/* Дополнительные материалы */}
        <AdditionalMaterialsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-fuchsia-50 dark:bg-fuchsia-900/20 border border-fuchsia-100 dark:border-fuchsia-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalcName}
                onChange={(e) => setSaveCalcName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать плитку'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать расчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;